import type {
  ScenarioStoryDefinition,
  StoryEntry,
} from "../../shared/index.js";
import {
  getStoryBackdropUrl,
  getStoryPortraitUrl,
} from "../assets/storyArt.js";

export interface StoryDirectorElements {
  overlay: HTMLElement;
  backdrop: HTMLElement;
  portrait: HTMLElement;
  portraitImage: HTMLImageElement;
  portraitSymbol: HTMLElement;
  speaker: HTMLElement;
  speakerRole: HTMLElement;
  text: HTMLElement;
  progress: HTMLElement;
  autoButton: HTMLButtonElement;
  historyDialog: HTMLDialogElement;
  historyList: HTMLOListElement;
  onEntry?: (entry: StoryEntry) => void;
  onVisibilityChange?: (visible: boolean) => void;
}

interface StoryHistoryEntry {
  speaker: string;
  text: string;
}

export class StoryDirector {
  private sequence: readonly StoryEntry[] = [];
  private index = 0;
  private backdropId = "dark";
  private onComplete: (() => void) | null = null;
  private auto = false;
  private autoTimer = 0;
  private renderedIndex = -1;
  private autoSpeed = 1;
  private readonly history: StoryHistoryEntry[] = [];

  public constructor(
    private story: ScenarioStoryDefinition,
    private readonly elements: StoryDirectorElements,
  ) {}

  public setStory(story: ScenarioStoryDefinition): void {
    this.reset();
    this.story = story;
  }

  public get isPlaying(): boolean {
    return !this.elements.overlay.classList.contains("hidden");
  }

  public play(sequence: readonly StoryEntry[], onComplete?: () => void): void {
    this.clearAutoTimer();
    this.sequence = sequence;
    this.index = 0;
    this.renderedIndex = -1;
    this.onComplete = onComplete ?? null;
    if (sequence.length === 0) {
      this.finish();
      return;
    }
    this.elements.overlay.classList.remove("hidden");
    this.elements.onVisibilityChange?.(true);
    this.render();
  }

  public advance(): void {
    if (!this.isPlaying) return;
    this.clearAutoTimer();
    if (this.index + 1 < this.sequence.length) {
      this.index += 1;
      this.render();
      return;
    }
    this.finish();
  }

  public skip(): void {
    if (!this.isPlaying) return;
    this.finish();
  }

  public toggleAuto(): void {
    this.auto = !this.auto;
    this.elements.autoButton.classList.toggle("selected", this.auto);
    this.elements.autoButton.setAttribute("aria-pressed", String(this.auto));
    this.clearAutoTimer();
    if (this.auto && this.isPlaying) this.scheduleAuto();
  }

  public setAutoSpeed(speed: number): void {
    this.autoSpeed = Math.max(0.5, Math.min(2, speed));
    if (this.auto && this.isPlaying) this.scheduleAuto();
  }

  public showHistory(): void {
    this.elements.historyList.innerHTML = this.history.map((entry) => {
      const speaker = entry.speaker
        ? "<strong>" + escapeHtml(entry.speaker) + "</strong>"
        : "<strong>語り</strong>";
      return "<li>" + speaker + "<p>" + escapeHtml(entry.text) + "</p></li>";
    }).join("");
    this.elements.historyDialog.showModal();
  }

  public reset(): void {
    this.clearAutoTimer();
    this.sequence = [];
    this.index = 0;
    this.renderedIndex = -1;
    this.backdropId = "dark";
    this.onComplete = null;
    this.auto = false;
    this.elements.autoButton.classList.remove("selected");
    this.elements.autoButton.setAttribute("aria-pressed", "false");
    this.elements.overlay.classList.add("hidden");
    this.elements.onVisibilityChange?.(false);
    this.history.length = 0;
    if (this.elements.historyDialog.open) this.elements.historyDialog.close();
  }

  private render(): void {
    const entry = this.sequence[this.index];
    if (!entry) return;
    const firstRender = this.renderedIndex !== this.index;
    if (entry.backdrop) this.backdropId = entry.backdrop;
    const backdrop = this.story.backdrops[this.backdropId]
      ?? this.story.backdrops.dark;
    const backdropUrl = backdrop ? getStoryBackdropUrl(backdrop.imageId) : "";
    this.elements.backdrop.style.backgroundImage = backdropUrl
      ? "url(\"" + backdropUrl + "\")"
      : "none";
    this.elements.backdrop.dataset.mode = backdrop?.mode ?? "dark";
    this.elements.backdrop.setAttribute("aria-label", backdrop?.label ?? "暗闇");

    const titleLine = entry.kind === "title";
    this.elements.overlay.classList.toggle("title-line", titleLine);
    if (titleLine) {
      this.elements.overlay.classList.add("no-speaker-portrait");
      this.elements.speaker.textContent = entry.eyebrow;
      this.elements.speakerRole.textContent = "";
      this.elements.text.textContent = entry.text;
      this.hidePortrait();
      this.recordHistory(entry.eyebrow, entry.text);
    } else {
      const speaker = this.story.speakers[entry.speaker] ?? {
        id: entry.speaker,
        name: entry.speaker,
        role: "",
        symbol: "",
        portraitId: "",
      };
      const narrator = entry.speaker === "narrator";
      const hidePortrait = narrator || Boolean(backdrop?.hidePortraits);
      this.elements.overlay.classList.toggle("no-speaker-portrait", hidePortrait);
      this.elements.speaker.textContent = narrator ? "語り" : speaker.name;
      this.elements.speakerRole.textContent = narrator ? "" : speaker.role;
      this.elements.text.textContent = entry.text;
      if (hidePortrait) {
        this.hidePortrait();
      } else {
        this.renderPortrait(speaker.portraitId, speaker.symbol, entry.speaker);
      }
      this.recordHistory(speaker.name, entry.text);
    }

    if (firstRender) this.elements.onEntry?.(entry);

    this.elements.progress.textContent =
      String(this.index + 1) + " / " + String(this.sequence.length);
    this.scheduleAuto();
  }

  private renderPortrait(
    portraitId: string,
    symbol: string,
    speakerId: string,
  ): void {
    const portraitUrl = getStoryPortraitUrl(portraitId);
    this.elements.portrait.dataset.speaker = speakerId;
    this.elements.portrait.classList.toggle("no-image", !portraitUrl);
    this.elements.portraitImage.src = portraitUrl;
    this.elements.portraitImage.alt = "";
    this.elements.portraitSymbol.textContent = symbol;
  }

  private hidePortrait(): void {
    this.elements.portrait.classList.add("no-image");
    this.elements.portraitImage.src = "";
    this.elements.portraitSymbol.textContent = "";
  }

  private recordHistory(speaker: string, text: string): void {
    if (this.renderedIndex === this.index) return;
    this.history.push({ speaker, text });
    this.renderedIndex = this.index;
  }

  private scheduleAuto(): void {
    this.clearAutoTimer();
    if (!this.auto || !this.isPlaying) return;
    const entry = this.sequence[this.index];
    const length = entry?.text.length ?? 0;
    const delay = Math.min(5200, Math.max(1500, 900 + length * 58)) / this.autoSpeed;
    this.autoTimer = window.setTimeout(() => this.advance(), delay);
  }

  private clearAutoTimer(): void {
    if (this.autoTimer) window.clearTimeout(this.autoTimer);
    this.autoTimer = 0;
  }

  private finish(): void {
    this.clearAutoTimer();
    this.elements.overlay.classList.add("hidden");
    this.elements.onVisibilityChange?.(false);
    this.sequence = [];
    this.index = 0;
    this.renderedIndex = -1;
    const callback = this.onComplete;
    this.onComplete = null;
    callback?.();
  }
}

function escapeHtml(value: string): string {
  return value
    .replaceAll("&", "&amp;")
    .replaceAll("<", "&lt;")
    .replaceAll(">", "&gt;")
    .replaceAll("\"", "&quot;")
    .replaceAll("'", "&#039;");
}
